import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { ABOUT_IMAGE_1, SCORECARD_PDF, SCORECARD_FILENAME } from "@/lib/constants";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function AboutBlock() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { name, email, source: "revenue_leak_audit_scorecard" });
      triggerDownload(SCORECARD_PDF, SCORECARD_FILENAME);
      toast.success("Your Revenue Leak Audit Scorecard is downloading now.");
      setTimeout(() => navigate("/thank-you"), 600);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="home-about-block" className="pt-28 lg:pt-36 pb-14 lg:pb-16 bg-cream">
      <div className="container-editorial">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Image */}
          <Reveal as="div" className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={ABOUT_IMAGE_1} alt="Sarah Atkinson" className="w-full h-full object-cover object-[center_top]" />
            </div>
            <p className="font-ui uppercase tracking-[0.18em] text-[12px] font-semibold text-charcoal/70 mt-5">
              — Sarah Atkinson · Founder
            </p>
          </Reveal>

          {/* Story */}
          <Reveal as="div" delay={120} className="lg:col-span-4 lg:pt-4">
            <p className="overline mb-5">About Sarah</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-navy leading-[1.1]">
              I see what others miss — so you can grow with{" "}
              <em className="font-serif italic text-sage">confidence.</em>
            </h2>
            <p className="font-sans text-base text-charcoal/70 mt-7 leading-[1.8]">
              With years of hands-on experience in short-term rentals and operations, I help Airbnb owners
              and STR investors create clarity, improve performance, and build businesses that support the
              life they actually want.
            </p>
            <Link to="/about" data-testid="about-read-story" className="editorial-link mt-10 inline-flex">
              Read My Story <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Link>
          </Reveal>

          {/* Lead magnet — Scorecard */}
          <Reveal as="div" delay={240} className="lg:col-span-3 bg-ivory border border-warmGray/20 p-8 lg:p-9" data-testid="about-lead-magnet">
            <p className="overline mb-4">Free Scorecard</p>
            <h3 className="font-serif text-navy text-[1.4rem] leading-[1.25] mb-4">
              The Revenue Leak Audit Scorecard
            </h3>
            <p className="font-sans text-[14px] text-charcoal/70 mb-7 leading-[1.7]">
              A strategic self-assessment — surface the friction quietly limiting bookings, profitability, and growth.
            </p>
            <form onSubmit={submit} className="space-y-5" data-testid="lead-magnet-form">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="lead-magnet-name"
                className="input-editorial"
              />
              <input
                type="email"
                required
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="lead-magnet-email"
                className="input-editorial"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="lead-magnet-submit"
                className="btn-gold w-full justify-center disabled:opacity-60"
              >
                {loading ? "Sending..." : (<>Download the Scorecard <Download className="w-4 h-4" /></>)}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
